import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { useDensityClass } from '@/design/density';
import { cx } from './cx';
import { Spinner } from './Spinner';

type ButtonVariant = 'primary' | 'commit' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * `brand` navigates. `action` commits.
 *
 * The rule lives here rather than in a style guide because conventions drift
 * and variants do not. `commit` uses action-600, never action-500: a white
 * label on 500 measures ~2.5:1.
 *
 * Which colour a commitment gets is decided by *whose* commitment it is.
 * Green is the candidate's side of the site — search, apply, create a profile.
 * Orange is the employer's — hire workers, post a vacancy, register a company.
 * Previously both wore the same orange, so "Search jobs" and "Hire workers"
 * sat side by side in the hero at identical weight and colour, asking a
 * visitor to pick a path the design gave them no way to tell apart.
 */
export const COMMIT_VARIANT_CLASSES =
  'bg-action-600 text-white hover:bg-action-700 focus-visible:outline-action-600';

/** The candidate's commitment: apply, search, create a profile. */
export const PRIMARY_VARIANT_CLASSES =
  'bg-brand-600 text-white hover:bg-brand-700 focus-visible:outline-brand-600';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: PRIMARY_VARIANT_CLASSES,
  commit: COMMIT_VARIANT_CLASSES,
  secondary: 'bg-white text-ink-800 ring-1 ring-inset ring-ink-500 hover:bg-ink-50',
  ghost: 'text-ink-600 hover:bg-ink-100',
  danger: 'bg-red-600 text-white hover:bg-red-700 focus-visible:outline-red-600',
};

const SIZES: Record<ButtonSize, Record<'public' | 'app', string>> = {
  sm: { public: 'px-3 py-1.5 text-sm', app: 'px-2.5 py-1 text-xs' },
  md: { public: 'px-4 py-2.5 text-sm', app: 'px-3 py-1.5 text-xs' },
  lg: { public: 'px-6 py-3 text-base', app: 'px-4 py-2 text-sm' },
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, disabled, className, children, ...rest },
  ref,
) {
  const sizeClass = useDensityClass(SIZES[size]);
  // A thumb in a factory canteen needs 44px; a clerk with a mouse needs density.
  const targetClass = useDensityClass({ public: 'min-h-11', app: 'min-h-8' });

  return (
    <button
      ref={ref}
      // A double-submitted application creates a duplicate row the recruiter
      // then has to clean up, so a loading button is always disabled.
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium',
        // A press that visibly gives is the cheapest confidence signal there
        // is — on a slow connection it is the only feedback between the tap and
        // the response, and it tells the user the tap registered.
        'transition duration-150 motion-safe:active:scale-[0.98]',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        sizeClass,
        targetClass,
        className,
      )}
      {...rest}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
});
